
// This lazyloader is a translation with modifications of the lazyLoad.js functionality from AKQA Frontline originally created by Lars Munkholm <Lars.Munkholm@akqa.com>
//
// ***************************************************
// ! Data-connections have been stripped out.
// ! Custom events also.
// ! Some functions have been simplified like onScroll.
// Lots of imports have been converted to jQuery methods since jQuery is already included in the project.
// ! The breakpointIndex and detection has also been reworked to use existing functionality so the attribute names must correspond with the current breakpointIndex.
// ! Uses Promises (For decoding), Object.Assign (For merging objects) and Map (For mapping elements with attributes) and therefore requires (simple) polyfills for older browsers.
// ***************************************************
//

/**
 * **Lazy load element sources, background images and more.**
 *
 * Lazy load the source for `img`, `picture`, `audio`, `video`, `embed`, `iframe`, `script` and `link`.
 * If this is used on any other type of element, the utility will set the given source as a background image. Or you can
 * define your own loader function. The possibilities are endless 🎈
 *
 * Images are loaded and decoded asynchronously to prevent blocking of the main thread.
 *
 *
 * #### Setting the source
 *
 * You set the source with the attribute `data-lazy`. The syntax for both `src` and `srcset` is supported.
 *
 * If you want to use `srcset` (only applicable for images), just use the
 * [srcset syntax for img](https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images)
 * or the [srcset syntax for picture](https://www.w3schools.com/tags/att_source_srcset.asp).
 *
 * Otherwise you should just set the source as a path to a file. `data-lazy="path/to/file.ext"`.
 *
 * Setting different sources for specific breakpoints is done with a comma separated list or JSON:
 * - `data-lazy="1: small.jpg, 3: medium.jpg, 5: large.jpg"`.
 * - `data-lazy='{ "0": "mobile.jpg", "2": "tablet.jpg", "4": "desktop.jpg" }'`.
 * 
 * #### Setting placeholders
 *
 * You can set placeholders using the attribute `data-lazy-placeholder`. By doing this, you can prevent content reflow,
 * where the content below or around lazy loaded content get moved around to make room for the newly loaded content.
 * Reflow is a user-blocking operation, which slows down the browser by forcing it to recalculate the layout of any
 * element that changes position.
 *
 * Placeholders can be defined with a path to the file, that you want to show until the correct source is loaded. Or you
 * can set it to an aspect ratio, which will generate an inline SVG source.
 *
 * - A single aspect ratio<br>`data-lazy-placeholder="480x320"`.
 * - A single path<br>`data-lazy-placeholder="path/to/placeholder.jpg"`.
 * - Breakpoint specific list<br>`data-lazy-placeholder="0: 480x320, 2: 768x500, 4: path/to/placeholder.jpg"`.
 * - Breakpoint specific JSON<br>`data-lazy-placeholder='{ "0": "image.jpg", "2": "768x500", "4": "1540x700" }'`.
 *
 *
 * @module utils/dom/lazyLoad
 * @author Lars Munkholm <lars.munkholm@akqa.com>
 *
 *
 * @example <caption>Basic JavaScript for lazy loading anything:</caption>
 *
 * // Lazy load sources for elements with the .lazy class name.
 * lazyLoad(".lazy");
 *
 * // Lazy load anything with the "data-lazy" attribute.
 * lazyLoad();
 *
 *
 * @example <caption>Possible settings in JavaScript:</caption>
 *
 * // This is bonkers, but you can do it if you'd like
 * lazyLoad(".slow", {
 *     classNames: {
 *         base: "slow",
 *         initialized: "init",
 *         loading: "getting",
 *         loaded: "got",
 *         error: "not-got",
 *         wrapper: "parent",
 *     },
 *     loadManually: true,
 *     loadImmediately: true,
 *     reloadOnResize: false,
 *     onLoading: function(element) { return element.style.opacity = .5 },
 *     onLoaded: [
 *         function(element) { return window.console.log("Callback 1 for", element)},
 *         function(element) { return window.console.log("Callback 2 for", element)},
 *     ],
 *     onError: function(element){ return element.style.display = "none" },
 *     loaderFunction: null,
 * });
 *
 *
 * @example
 * <caption>
 * JavaScript for loading sources immediately. That's not lazy at all, but the utility will still be used for setting
 * the relevant source and handling responsiveness:
 * </caption>
 * lazyLoad(images, { loadImmediately: true });
 *
 *
 * @example <caption>Basic HTML for lazy loading a responsive image:</caption>
 * {@lang html}
 * <img class="lazy" alt="Dummy alt text"
 *     src="fallback.jpg"
 *     data-lazy="
 *         0: path/to/image/xxs.jpg,
 *         1:  path/to/image/xs.jpg,
 *         2:  path/to/image/sm.jpg,
 *         3:  path/to/image/md.jpg,
 *         4:  path/to/image/lg.jpg,
 *         5:  path/to/image/xl.jpg">
 *
 *
 * @example <caption>It is possible to define placeholders for images to prevent reflow:</caption>
 * {@lang html}
 * // This automatically generates an inline SVG in the correct aspect ratio.
 * <img class="lazy" alt="Dummy alt text"
 *     data-lazy="path/to/image.jpg"
 *     data-lazy-placeholder="1000x500">
 *
 * // Define different breakpoint specific aspect ratios.
 * <img class="lazy" alt="Dummy alt text"
 *     data-lazy="0: path/to/image/xxs.jpg, 3: path/to/image/md.jpg, 5: path/to/image/xl.jpg"
 *     data-lazy-placeholder="0: 480x200, 3: 768x400, 5: 1540x500">
 *
 * // Define your own fallback sources.
 * // Be aware that this will make requests for the placeholders on page load.
 * <img class="lazy" alt="Dummy alt text"
 *     data-lazy="0: path/to/image/xxs.jpg, 3: path/to/image/md.jpg"
 *     data-lazy-placeholder="0: path/to/placeholder/xxs.jpg, 3: path/to/placeholder/md.jpg">
 *
 *
 * @example
 * <caption>
 * HTML for lazy loading a responsive background image:
 * </caption>
 * {@lang html}
 * <div class="lazy"
 *      style="background-image: url(fallback.jpg)"
 *      data-lazy="0: very-small.jpg, 3: medium.jpg, 5: very-large.jpg">
 *     ...
 * </div>
 *
 *
 * @example <caption>Other examples of HTML:</caption>
 * {@lang html}
 * <video class="lazy" autoplay
 *        data-lazy="good-quality.mp4"
 * </video>
 *
 * <audio class="lazy" autoplay data-lazy="play-me-later.mp3"></audio>
 *
 * <embed class="lazy" data-lazy="lazy-embed.mp3">
 *
 * <script data-lazy="lazy-script.js"></script>
 *
 * <link rel="stylesheet" type="text/css" data-lazy="lazy-styles.css">
 *
 * <iframe class="lazy" data-lazy="
 *     0: small-layout.html,
 *     4: bigger-layout.html
 * "></iframe>
 *
 * <picture class="lazy" data-lazy-placeholder="0: 465x250, 3: 768x350">
 *     <source media="(min-width: 768px)" data-lazy="768x350.jpg">
 *     <source media="(min-width: 465px)" data-lazy="465x250.jpg">
 *     <img alt="Dummy alt text">
 * </picture>
 *
 */

(function ($, window) {
    "use strict";

    // Temp placement
    function wrapElement(existingElement, newWrapper) {
        existingElement.parentNode.insertBefore(newWrapper, existingElement);
        newWrapper.appendChild(existingElement);
    };

    function isElementInViewport(element, expandMargin) {
        if (!expandMargin) expandMargin = 0;

        var rect = element.getBoundingClientRect();
        var expandMarginX = isNaN(expandMargin) ? expandMargin.x : expandMargin;
        var expandMarginY = isNaN(expandMargin) ? expandMargin.y : expandMargin;
        var spanY = rect.top + Math.max(rect.height, 1) + expandMarginY;
        var spanX = rect.left + Math.max(rect.width, 1) + expandMarginX;
    
        return (spanY >= Math.min(0, expandMarginY) + 1 && rect.top <= window.innerHeight + expandMarginY - 1)
            && (spanX >= Math.min(0, expandMarginX) + 1 && rect.left <= window.innerWidth + expandMarginX - 1);
    };

    function createElement(tagName, options) {
        if (!options) options = {};

        var newElement = document.createElement(tagName);
    
        $(options).each(function(optionKey, optionValue) {

            switch (optionKey) {

                case "className":
                    $(newElement).addClass(optionValue || "");
                    break;

                case "attributes":
                    $(newElement).attr(optionValue);
                    break;

                case "html":
                    // We'll only set the HTML contents if the "text" property hasn't ALSO been set.
                    if (!options.text && typeof optionValue !== "undefined") {
                        if (typeof optionValue === "string") {
                            newElement.innerHTML = optionValue;
                        } else {
                            newElement.append(optionValue);
                        }
                    }
                    break;

                case "text":
                    newElement.innerText = options.text;
                    break;
    
                case "style":
                    if (typeof options.style === "object") {
                        $(newElement).css(optionValue);
                    } else {
                        $(newElement).attr(optionKey, optionValue);
                    }
                    break;
    
                default:
                    // Any unknown option key will be treated as a raw attribute.
                    $(newElement).attr(optionKey, optionValue);
            }
    
        });
    
        return newElement;
    
    }

    DSV.ui.lazyLoad = {
        // init
        defaultSettings: {
            classNames: {
                base: "lazy",
                initialized: "initialized",
                loading: "loading",
                loaded: "loaded",
                error: "error",
                wrapper: "wrapper",
            },
            loadManually: false,
            loadImmediately: false,
            reloadOnResize: true,
        },

        globalSettings: {
            bufferMarginPx: 100,
            onScrollThrottleMs: 250,
        },

        isEventListenerSet: {
            scroll: false,
            resize: false,
        },

        elementsLoaded: new Map(),
        elementsNotLoaded: new Map(),
        elementsHandledManually: new Map(),

        placeholderAttribute: "data-lazy-placeholder",

        // An array of all possible data attributes defining sources.
        sourceAttribute: "data-lazy",
        allSourceAttributes: ["data-lazy"],

        // From _variables.scss
        // 0: $mqTablet: < 481;
        // 1: $mqDesktop: < 769;
        // 2: $mqLarge: < 1024;
        // 3: $mqXLarge: < 1280; 
        // 4: $mqLaptop: < 1366;
        // 5: $mqXXLarge: < 1440 || > 1440;
        // Left side is the naming in the attribute
        // Right side is the current breakpoint number from dsv.tools.screenmode.get()
        breakpointIndex: {
            0: 1,
            1: 2,
            2: 3,
            3: 4,
            4: 5,
            5: 6
        },

        LAZY_EVENT_TARGET: document.documentElement,

        containerScrollListeners: [],

        /**
        * Add a callback when the element is scrolled.
        *
        * @param {Element|Element[]|NodeList|Window} elementList - List of element(s) to add callback to.
        * @param {function|function[]} callbackList - Function or array of functions to add.
        * @param {number} [throttleValueMs=0] - Optional throttle value, given in milliseconds. If omitted, no throttling is employed.
        *
        */
        onScroll: function(elementList, callbackList, throttleValueMs) {
            throttleValueMs = typeof throttleValueMs !== 'undefined' ? throttleValueMs : 0;
            var that = this;
            var callbackListContainer = [];
            if (typeof callbackList  === 'function') {
                callbackList = callbackListContainer.push(callbackList);
            } else {
                callbackListContainer = callbackList;
            }
            $(elementList).each(function(index, element) {
                $.each(callbackListContainer, function(index, callback) {
                    var callbackToExecute = callback;
                    var active = false;
                    that.containerScrollListeners.push(element);
                    $(element).on('scroll.lazyload', function() {
                        if (active === false) {
                            active = true;
                            setTimeout(function() {
                                callbackToExecute();
                                active = false;
                            }, throttleValueMs)
                        }
                    })
                })
    
            })
        },

        /**
         * Try to decode the image, after it's loaded, and resolve the Promise.
         *
         * @private
         * @param {Element} newImage
         * @returns {Promise<Image>}
         */
        decodeImage: function(newImage) {
            return ("decode" in newImage) ? newImage.decode().then(function() {return newImage}, function(){return newImage}) : Promise.resolve(newImage);
        },

        /**
         * Load an image, and return a Promise that resolves once the image is loaded.
         *
         * @param {string} source - The path to the image.
         * @param {Object} [options] - Object of settings.
         * @param {boolean} [options.useSrcset=false] - Set to true to use the `srcset` attribute instead of `src`.
         * @param {string} [options.sizes=""] - If you're using `srcset`, you can also add a `sizes` attribute.
         * @returns {Promise<Image>} Promise that will resolve with the loaded image once it's ready.
         */
        loadImage: function(source) {
            // !: Takes an extra hidden options parameter with a default of {}
            var settings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
            var useSrcset = settings.length > 1 && typeof settings.useSrcset !== undefined ? settings.useSrcset : false;
            var sizes = settings.length > 1 && typeof settings.sizes !== undefined ? settings.useSrcset : false;

            var newImage = new Image();
            var that = this;
            if (useSrcset && sizes && "sizes" in newImage) {
                newImage.sizes = sizes;
            }
            return new Promise(function(resolve, reject) {
                $(newImage).on("load", function() {
                    $(newImage).off("load error");
                    that.decodeImage(newImage).then(function(image) { return resolve(image)});
                });

                $(newImage).on("error", function() {
                    $(newImage).off("load error");
                    reject();
                });

                newImage[useSrcset ? "srcset" : "src"] = source;
            });
        },
        /**
         * Check if an element needs a wrapper.
         *
         * @param {Element|HTMLAudioElement} element
         * @returns {boolean}
         */
        elementNeedsWrapper: function(element) { return $(element).is("script, link, embed") || ($(element).is("audio") && !element.controls) },

        /**
         * Detect and load elements that are visible in the viewport.
         */
        detectElementsInViewport: function() {
            var that = this;
            if (this.elementsNotLoaded && this.elementsNotLoaded.size) {                // Loop through a map
                that.elementsNotLoaded.forEach(function(data, index) {
                    if (isElementInViewport(data.elements.outer, that.globalSettings.bufferMarginPx)) {
                        var newSource = that.getSourcesFromAttribute(data);
                        that.setSource(newSource, data);
                    }
                })
            } else {
                $.each(that.containerScrollListeners, function(index, element) {
                    $(element).off("scroll.lazyload");
                })
                this.isEventListenerSet.scroll = false;
            }
        },
        /**
         * Get sources from the most relevant attribute.
         *
         * @param {LazyLoadData} data
         * @param {string} [attributeName] - Optional attribute name. If none is defined, we'll find the most appropriate.
         * @returns {{sources: string[], breakpointCount: number, attribute: string}}
         */
        getSourcesFromAttribute: function(data, attributeName) {
            var that = this;
            var useSourceChildren = data.useSourceChildren,
            elements = data.elements;
            var defaultSourceName = "default";
            var sources = [];
            var isSrcsetSyntax = true;
            var breakpointCount = 0;
        
            var sourceElements = elements.base;
        
            if (useSourceChildren && (!attributeName || !elements.base.hasAttribute(attributeName))) {
                sourceElements = elements.sourceChildren;
            }
        
        
            $(sourceElements).each(function(_index, sourceElement) {
                var newSource = {};
                var sourceData;
                var closestBreakpoint = 0;
                var closestBreakpointName = defaultSourceName;
        
                // An attribute name was supplied. Let's get whatever data is in that attribute.
                if (attributeName) {
                    sourceData = sourceElement.getAttribute(attributeName);
                }
        
                // No attribute name was supplied - let's find one that suits the network speed,
                // and get whatever data is in that attribute.
                else {
                    sourceData = sourceElement.getAttribute(that.sourceAttribute); // If no slow speed attributes were found, use the default attribute.
                }
        
                if (sourceData) {
                    // Parse the data from the attribute.
                    // This can be either JSON or comma separated like "xxs: [...], md: [...], xl: [...]".

                    try {
                        newSource = JSON.parse(sourceData);
                    } catch (error) {
                        isSrcsetSyntax = /\s[0-9]+(w|x)\s*(,|$)/g.test(sourceData);
        
                        if (isSrcsetSyntax) {
                            var srcsetFileRegex = /((?:[^\s,][^,]+?(?=\s[0-9]+(?:w|x)\s*(,|$)))|(^(?!\s[0-9]+(?:w|x))).*$)/g;
                            newSource[defaultSourceName] = sourceData.replace(srcsetFileRegex, function(match) {return encodeURI(match)});
                        } else {
                            var dataArray = sourceData.split(/,\s?/);
                            $(dataArray).each(function(_index, item) {
                                if (item) {
                                    var trimmedItem = item.trim();
                                    var colonIndex = trimmedItem.indexOf(":");
                                    var key = trimmedItem.substring(0, colonIndex);
                                    var value = trimmedItem;

                                    if (key in that.breakpointIndex) {
                                        value = trimmedItem.substring(colonIndex + 1);
                                    } else {
                                        key = defaultSourceName;
                                    }
        
                                    value = value.trim().replace(/^"|'|"|'$/g, "");
        
                                    newSource[key] = encodeURI(value);
                                }
                            });
                        }
                    }
        
                    // Find the most relevant source for the current breakpoint.
                    var sourcesKeys = Object.keys(newSource);
                    breakpointCount = Math.max(breakpointCount, sourcesKeys.length);
        
                    if (breakpointCount === 1) {
                        closestBreakpointName = sourcesKeys[0];
                    } else {                        
                        $.each(newSource, function(key, source) {
                            var thisBreakpoint = that.breakpointIndex[key];
                            if (thisBreakpoint && (
                                thisBreakpoint === DSV.tools.screenMode.get() || (
                                    closestBreakpoint !== DSV.tools.screenMode.get() &&
                                    (
                                        !closestBreakpoint ||
                                        (thisBreakpoint <= DSV.tools.screenMode.get() && thisBreakpoint > closestBreakpoint)
                                    )
                                )
                            )) {
                                closestBreakpoint = thisBreakpoint;
                                closestBreakpointName = key;
                            }
                        });
                    }
                }
        
                sources.push(newSource[closestBreakpointName]);
            });
        
        
            return {
                attribute: $(elements.base).is("link") ? "href" : isSrcsetSyntax ? "srcset" : "src",
                breakpointCount: breakpointCount,
                sources: sources,
            };
        },

        /**
         * Get the most appropriate placeholder source.
         *
         * The value of the data attribute must be `path/to/source.jpg` or a comma separated list (or JSON) of breakpoints
         * `xxs: path/to/xxs.jpg, md: path/to/xxs.jpg`. Instead of paths you can also define aspect ratios which will generate
         * an SVG data URI: `4x3` or `sm: 4x3, md: 16x9, lg: 16x10` or `{ "sm": "4x3", "md": "16:9" }`.
         *
         * @param {Element} element
         * @param {LazyLoadData} data
         * @returns {string}
         */
        getPlaceholder: function(element, data) {
            var source;
            var x;
            var y;
        
            if (element.hasAttribute(this.placeholderAttribute)) {
                source = this.getSourcesFromAttribute(data, this.placeholderAttribute);
        
                // Test if the source string matches the pattern {number}x{number}
                if (/^[0-9]+x[0-9]+$/.test(source.sources[0])) {
                    var width = source.sources[0].split("x")[0];
                    var height = source.sources[0].split("x")[1];
                    x = parseFloat(width);
                    y = parseFloat(height);
                }
            } else {
                x = 1;
                y = 1;
            }
        
        
            return isNaN(x) || isNaN(y)
                ? source
                : {
                    attribute: "src",
                    breakpointCount: source ? source.breakpointCount : 1,
                    sources: ['data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="' + x + '" height="' + y + '" viewBox="0 0 '+ x + ' ' + y + '"%3E%3C/svg%3E'],
                };
        },

        /**
         * Set placeholder on an element.
         *
         * @param {LazyLoadData} data
         */
        setPlaceholder: function(data) {
            var elements = data.elements,
            hasLoaded = data.hasLoaded;
            if (!hasLoaded && elements.control) {
                var isImgWithoutSrc = $(elements.control).is("img") && !elements.control.src;
                var elementWithPlaceholderData = elements.base.hasAttribute(this.placeholderAttribute)
                    ? elements.base
                    : elements.control.hasAttribute(this.placeholderAttribute)
                        ? elements.control
                        : undefined
        
                // Continue if the element has an attribute with a defined placeholder,
                // or if it's an image with no src. Src is mandatory, you know.
                if (elementWithPlaceholderData || isImgWithoutSrc) {
                    var newSource = this.getPlaceholder(elementWithPlaceholderData || elements.control, data);
                    this.setSource(newSource, data, true);
                }
            }
        },

        /**
         * Update source attribute.
         *
         * @param {Element} element
         * @param {string} attribute
         * @param {string} source
         */
        updateSourceAttribute: function(element, attribute, source) {
            if (source && element[attribute] !== source) {
                element[attribute] = source;
            }
        },

        /**
         * Set event listeners for load and error.
         *
         * @param {LazyLoadData} data
         * @param {boolean} [isPlaceholder=false]
         * @param {boolean} [useCanPlay=false]
         */
        setLoadEventListeners: function(data, options) {
            var isPlaceholder = options.isPlaceholder;
            var useCanPlay = options.useCanPlay;
            isPlaceholder = typeof isPlaceholder !== 'undefined' ? isPlaceholder : false;
            useCanPlay = typeof useCanPlay !== 'undefined' ? useCanPlay : false;

            var element = data.elements.control;
            var loadEvent = useCanPlay ? "canplay" : "load";
        
            var that = this;

            $(element).on(loadEvent, function() {
                $(element).off(loadEvent + ' error');
        
                if (!isPlaceholder) {
                    that.setClassName(data, "loaded");
                }
            });

            $(element).on("error", function() {
                $(element).off(loadEvent + ' error');

                if (!isPlaceholder) {
                    that.setClassName(data, "error");
                }
            });
        },

        /**
         * Set the source of an element or trigger the custom loader function.
         *
         * @param {Object} newSource
         * @param {LazyLoadData} data
         * @param {boolean} data.useSourceChildren - Set to true if we are setting the source to a placeholder, and the classes won't change and notifiers won't be executed.
         * @param {boolean} [isPlaceholder=false]
         */
        setSource: function(newSource, data, isPlaceholder) {
            isPlaceholder = typeof isPlaceholder !== 'undefined' ? isPlaceholder : false;

            var useSourceChildren = data.useSourceChildren;
            var loaderFunction = data.loaderFunction;
            var elements = data.elements;

            var that = this;
        
            // Only continue if the new source is not the same as the one already in use.
            if (loaderFunction && !isPlaceholder) {
        
                this.setClassName(data, "loaded");
                loaderFunction(elements.base);
        
            } else {
        
                // Element uses source tags (picture, video, audio)
                if (useSourceChildren) {
                    if (!isPlaceholder) {
                        this.setClassName(data, "loading");
                    }
        
                    // Picture (load and decode)
                    if ($(elements.base).is("picture")) {
                        var elementClone = elements.base.cloneNode(true);
                        var elementCloneImg = elementClone.querySelector("img");
        
                        $(elementCloneImg).on("load", function() {
                            $(elementCloneImg).off("load error");
        
                            that.decodeImage(elementCloneImg).then(function() {
                                if (isPlaceholder) {
                                    that.updateSourceAttribute(elements.control, "src", newSource.sources[0]);
                                } else {
                                    var i = 0;
                                    $(elements.sourceChildren).each(function(index, sourceTag) {
                                        that.updateSourceAttribute(sourceTag, "srcset", newSource.sources[i]);
                                        i += 1;
                                    });
        
                                    that.setClassName(data, "loaded");
                                }
                            });
                        });
        
                        $(elementCloneImg).on("error", function() {
                            $(elementCloneImg).off("load error");
        
                            if (!isPlaceholder) {
                                that.setClassName(data, "error");
                            }
                        });
        
                        if (isPlaceholder) {
                            that.updateSourceAttribute(elementCloneImg, "src", newSource.sources[0]);
                        } else {
                            var i = 0;
                            $(elementClone.querySelectorAll("source")).each(function(index, sourceTag) {
                                that.updateSourceAttribute(sourceTag, "srcset", newSource.sources[i]);
                                i += 1;
                            });
                        }
                    }
        
                    // Audio and video
                    else {
                        that.setLoadEventListeners(data, { isPlaceholder: isPlaceholder, useCanPlay: true });
        
                        $(elements.sourceChildren).each(function(index, sourceTag) {
                            that.updateSourceAttribute(sourceTag, "src", newSource.sources[0])
                        });
        
                        elements.base.load();
                    }
                }
        
                // Element doesn't use source tags
                else {
                    var isImage = $(elements.base).is("img");
                    var isBackgroundImage = !$(elements.base).is("img, iframe, audio, video, embed, script, link");
                    var backgroundUrl = 'url(' + newSource.sources[0] + ')';
        
                    // Stop now if the found source is the same as the one currently in use
                    if (
                        isBackgroundImage
                            ? elements.outer.style.backgroundImage === backgroundUrl
                            : elements.control[newSource.sources.attribute] === newSource.sources[0]
                    ) {
                        return;
                    }
        
                    if (!isPlaceholder) {
                        that.setClassName(data, "loading");
                    }
        
                    // Handle the loading (or failing thereof) and decoding of images
                    if (isImage || isBackgroundImage) {
                        var useSrcset = newSource.attribute === "srcset";
                        that.loadImage(newSource.sources[0], { useSrcset: useSrcset, sizes: elements.base.sizes })
                            .then(function() {
                                if (isBackgroundImage) {
                                    elements.outer.style.backgroundImage = backgroundUrl;
                                } else {
                                    elements.control.srcset = "";
                                    elements.control[newSource.attribute] = newSource.sources[0];
                                }

                                if (!isPlaceholder) {
                                    that.setClassName(data, "loaded");
                                }
                            })
                            .catch(function() {
                                if (!isPlaceholder) {
                                    that.setClassName(data, "error");
                                }
                            });
        
                    }
        
                    // Handle embed - sadly, we can't rely on the events to be fired.
                    // The load and error events work with some content in embed elements, but not with everything. :-(
                    else if ($(elements.base).is("embed")) {
        
                        that.setLoadEventListeners(data, { isPlaceholder: isPlaceholder, useCanPlay: true });
                        that.updateSourceAttribute(elements.control, "src", newSource.sources[0]);
        
                        if (!isPlaceholder) {
                            that.setClassName(data, "loaded");
                        }
        
                    }
        
                    // Handle the loading of audio and video
                    else if ($(elements.base).is("audio, video")) {
        
                        that.setLoadEventListeners(data, { isPlaceholder: isPlaceholder, useCanPlay: true });
                        that.updateSourceAttribute(elements.control, "src", newSource.sources[0]);
                        elements.base.load();
        
                    }
        
                    // Handle the loading of everything else
                    else {
        
                        that.setLoadEventListeners(data, { isPlaceholder: isPlaceholder });
                        that.updateSourceAttribute(elements.control, newSource.attribute, newSource.sources[0]);
        
                    }
                }
            }
        
            if (!isPlaceholder) {
                if (this.elementsNotLoaded.has(elements.base)) {
                    this.elementsNotLoaded.delete(elements.base);
                    this.elementsLoaded.set(elements.base, Object.assign({}, data, { hasLoaded: true } ));
                } else if (this.elementsHandledManually.has(elements.base)) {
                    this.elementsHandledManually.set(elements.base, Object.assign({}, data, { hasLoaded: true } ));
                }
            }
        },

        /**
         * Ensure that callbacks are in arrays.
         *
         * @param {function|function[]} callbackList
         * @returns {Array}
         */
        sanitizeCallbacks: function(callbackList) {
            return callbackList instanceof Array ? callbackList : typeof callbackList === "function" ? [callbackList] : [];
        },

        /**
         * Reset elements so they can be updated to match the current breakpoint when appropriate.
         */
        resetElementsWithResponsiveSources: function() {
            var that = this;
            this.elementsLoaded.forEach(function(data, element) {
                if (data.handleResponsive) {
                    if (data.loadImmediately) {
                        var newSource = that.getSourcesFromAttribute(data);
                        that.setSource(newSource, data);
                    } else {
                        that.elementsLoaded.delete(element);
                        that.elementsNotLoaded.set(element, data);
                    }
                }
            });
        
            if (this.elementsNotLoaded.size) {
                if (!this.isEventListenerSet.scroll) {
                    this.onScroll(window, this.detectElementsInViewport.bind(this), this.globalSettings.onScrollThrottleMs);
                    this.isEventListenerSet.scroll = true;
                }

                this.elementsNotLoaded.forEach(function(data, element) { 
                    return that.setPlaceholder(data); 
                });
        
                this.detectElementsInViewport();
            }
        
            if (this.elementsHandledManually.size) {
                this.elementsHandledManually.forEach(function(data, element) { return data.hasLoaded || that.setPlaceholder(data)});
            }
        },
        /**
         * Set class name on element.
         *
         * @param {LazyLoadData} data
         * @param {("initialized"|"loading"|"error"|"loaded"|null)} state - Set to "initialized", "loading", "error" or "loaded". Or null to remove all class names.
         */
        setClassName: function(options, state) {
            var classNames = options.classNames;
            var elements = options.elements;
            var classes = {
                initialized: classNames.base + '--' +classNames.initialized,
                loading: classNames.base + '--' + classNames.loading,
                loaded: classNames.base + '--' + classNames.loaded,
                error: classNames.base + '--' + classNames.error,
            };
        
            $(elements.outer).removeClass([classes.loading, classes.loaded, classes.error].concat(state ? [] : classes.initialized));
        
            if (state) {
                $(elements.outer).addClass(classes[state]);
            }
        },

        /**
         * Setup lazy load. If you wanna lazy load something, you've come to the right place.
         *
         * @param {string|NodeList|Element|Element[]} [elements] - The elements to lazy load or a selector to query. If none is given, a selector will be generated from all the possible data attributes.
         * @param {LazyLoadSettings} [options={}] - An object with settings.
         */
        lazyLoad: function(elements, options) {
            var that = this;
            options = typeof options !== 'undefined' ? options : {};
            var settings = Object.assign({}, 
                this.defaultSettings, 
                options, 
                {
                    classNames: Object.assign({}, 
                        this.defaultSettings.classNames, 
                        options && options.classNames),
                    onLoading: this.sanitizeCallbacks(options.onLoading),
                    onLoaded: this.sanitizeCallbacks(options.onLoaded),
                    onError: this.sanitizeCallbacks(options.onError)
                });

            var elementsToLoad;


            // Find elements to load from a given selector string
            // or create a selector string of all the possible data attributes.
            if (!elements || typeof elements === "string") {
                elementsToLoad = document.querySelectorAll(elements || '[' + that.sourceAttribute + ']');
            }
        
            // A NodeList or an array of elements was given.
            else if (typeof elements === "object" && elements.length) {
                elementsToLoad = elements;
            }
        
            // A single element was given.
            else if (elements instanceof Element) {
                elementsToLoad = [elements];
            }
        
            if (!elementsToLoad) {
                throw "lazyLoad's elements argument was not given as a string or a nodeList.";
            }
        
        
            if (elementsToLoad.length) {
                var setResizeEventListener = false;

                $(elementsToLoad).each(function(index, elementToLoad) {
                    var classNames = settings.classNames;
                    var loadManually = settings.loadManually;
                    var loadImmediately = settings.loadImmediately;
                    var reloadOnResize = settings.reloadOnResize;

                    var defaultElement = $(elementToLoad).is("source, track")
                        ? elementToLoad.parentElement
                        : elementToLoad;
                    var elements = {
                        base: defaultElement,
                        outer: defaultElement,
                        control: defaultElement,
                        sourceChildren: defaultElement,
        
                    };
                    var useSourceChildren = false;
        
        
                    // Only continue if element hasn't already been initialized.
                    if (
                        !that.elementsLoaded.has(elements.base) &&
                        !that.elementsNotLoaded.has(elements.base) &&
                        !that.elementsHandledManually.has(elements.base)
                    ) {
        
                        // Some elements need a wrapper.
                        if (that.elementNeedsWrapper(elements.base)) {
        
                            // If the element already has a wrapper, we should just skip it.
                            if ($(elements.base.parentElement).hasClass(classNames.base + '--' + classNames.wrapper)) {
                                return;
                            }
        
                            elements.outer = createElement("div", {
                                className: [
                                    classNames.base + '--' + classNames.wrapper,
                                    classNames.base + '--' + elements.base.tagName.toLowerCase(),
                                ],
                                style: $(elements.base).is("embed") ? {} : {
                                    display: "inline-block",
                                    visibility: "hidden",
                                    margin: 0,
                                    border: 0,
                                    padding: 0,
                                    height: 0,
                                    width: 0,
                                },
                            });
        
                            wrapElement(elements.base, elements.outer);
                        }
        
        
                        // Some elements have children of the source and track variety and need to be handled differently.
                        if ($(elements.base).is("picture, audio, video")) {
                            var sources = elements.base.querySelectorAll("source, track");
        
                            if (sources.length) {
                                elements.sourceChildren = sources;
                                useSourceChildren = true;
                            }
        
                            if ($(elements.base).is("picture")) {
                                elements.control = elements.base.querySelector("img");
                            }
                        }

                        var elementData = Object.assign({}, settings, {
                            handleResponsive: false,
                            useSourceChildren: useSourceChildren,
                            elements: elements,
                            hasLoaded: loadImmediately
                        });
                        elementData.handleResponsive = reloadOnResize && that.allSourceAttributes.reduce(function(isResponsive, attribute) {
                                return isResponsive || that.getSourcesFromAttribute(elementData, attribute).breakpointCount > 1;
                            }, false);

                        // If any of the elements have attributes for breakpoints,
                        // that must mean we should handle responsiveness,
                        // thus we'll need to listen for the window resize event.
                        if (elementData.handleResponsive) {
                            setResizeEventListener = true;
                        }

                        if (!$(elements.outer).hasClass(classNames.base)) {
                            $(elements.outer).addClass(classNames.base);
                        }

                        that.setClassName(elementData, "initialized");

                        // Store the element in the appropriate Map object.
                        (loadManually ? that.elementsHandledManually : loadImmediately ? that.elementsLoaded : that.elementsNotLoaded)
                            .set(elements.base, elementData);


                        // Load source immediately if that's what's up.
                        if (loadImmediately) {
                            var newSource = that.getSourcesFromAttribute(elementData);
                            that.setSource(newSource, elementData);
                        }
        
                        // Set placeholder SVGs for images. For IMG elements, src is a mandatory attribute,
                        // so if no src or placeholder is defined, it will be set to a blank 1x1px SVG.
                        else {
                            that.setPlaceholder(elementData);
                        }
                    }
                });
        
        
                // Show elements that are already visible in the viewport.
                that.detectElementsInViewport();
        
                // Set event listeners if they are needed and haven't been set already.
                if (!that.isEventListenerSet.scroll && that.elementsNotLoaded.size) {
                    that.onScroll(window, that.detectElementsInViewport.bind(this), that.globalSettings.onScrollThrottleMs);
                    that.isEventListenerSet.scroll = true;
                }
        
                if (!that.isEventListenerSet.resize && setResizeEventListener) {
                    var active = false;
                    $(window).on('resize', function() {
                        if (active === false) {
                            active = true;
                            setTimeout(function() {
                                that.resetElementsWithResponsiveSources();
                                active = false;
                            }, that.globalSettings.onScrollThrottleMs)
                        }
                    });
                    that.isEventListenerSet.resize = true;
                }
            }
        },

        /**
         * Remove lazy load functionality from element.
         *
         * @param {Element|Element[]|NodeList} elements
         */
        removeLazyLoad: function(elements) {
            var that = this;
            if (elements) {
                $(elements).each(function(index, element) {
                    var data = that.elementsLoaded.get(element) || that.elementsNotLoaded.get(element) || that.elementsHandledManually.get(element);
        
                    if (data) {
                        that.setClassName(data, null);
                        $(data.elements.control).off("load canplay error");
                        that.elementsLoaded.delete(element) || that.elementsNotLoaded.delete(element) || that.elementsHandledManually.delete(element);
                    }
                });
            }
        },

        setLazyLoadGlobalSettings: function(options) {
            globalSettings = Object.assign({},
                globalSettings,
                options
            );
        },

        init: function() {
            this.lazyLoad('.lazy');
            this.lazyLoad('.lazy--immediately', { loadImmediately: true });
        }
    };
})(jQuery, window);